import { useEffect, useState } from 'react';
import { Chip, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import { InfoCard, Link, Progress, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

type Change = { id: string; level: number; text: string; operation?: string; path?: string };
type PrChange = { url: string; title: string; repo: string; number: number; breaking: number; total: number; changes: Change[]; consumers: { name: string }[] };

/** PRs abiertos que cambian el contrato de esta API (oasdiff en el CI) y a cuántos consumidores afectan. */
export const ApiChangesCard = () => {
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [prs, setPrs] = useState<PrChange[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    (async () => {
      const base = await discovery.getBaseUrl('api-changes');
      const res = await fetch(`${base}/changes?api=${encodeURIComponent(stringifyEntityRef(entity))}`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setPrs(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [discovery, fetch, entity]);

  return (
    <InfoCard title="Cambios de contrato en PRs abiertos" subheader="Comparación OpenAPI del CI; se actualiza cada 10 min">
      {error && <WarningPanel title="No se pudieron cargar los cambios" message={error} />}
      {!prs && !error && <Progress />}
      {prs && !prs.length && <Typography variant="body2">Ningún PR abierto cambia este contrato.</Typography>}
      {prs && !!prs.length && (
        <List dense>
          {prs.map(pr => (
            <ListItem key={pr.url} alignItems="flex-start" divider>
              <ListItemText
                primary={
                  <>
                    <Link to={pr.url}>
                      {pr.repo.split('/').pop()}#{pr.number} {pr.title}
                    </Link>{' '}
                    <Chip
                      size="small"
                      color={pr.breaking ? 'secondary' : 'default'}
                      label={pr.breaking ? `${pr.breaking} rompen` : 'compatible'}
                    />{' '}
                    <Chip size="small" variant="outlined" label={`${pr.consumers.length} consumidor(es)`} />
                  </>
                }
                secondary={
                  <>
                    {pr.changes.slice(0, 5).map(c => (
                      <span key={`${c.id}${c.operation}${c.path}`} style={{ display: 'block' }}>
                        {c.level === 3 ? '⛔' : '•'} {c.operation} {c.path}: {c.text}
                      </span>
                    ))}
                    {pr.total > 5 && <span>… y {pr.total - 5} más en el PR</span>}
                    {!!pr.consumers.length && <span style={{ display: 'block' }}>Consumidores: {pr.consumers.map(c => c.name).join(', ')}</span>}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </InfoCard>
  );
};
